import _                                            from 'underscore';
import React, { ReactNode, PropsWithChildren }      from 'react';
import { ChartData, ChartType, ChartTypeRegistry, } from 'chart.js';
import { Divider }                                  from 'semantic-ui-react';

import { useChartReportingExport, useChartReportingView }  from '../../../../../../hooks';
import { Charting }                                        from '../../../../../../types/charting';
import { UseIf }                                           from '../../../../../components/display/EqualValues';
import { ChartReportingViewType, ChartReportViewControls } from './ChartReportViewControls';
import { DistributiveArray }                               from 'chart.js/dist/types/utils';

interface ChartReportWithControlsLayoutProps<T extends ChartType, D>
	extends Charting.Reporting.ChartReportBaseProps<T>,
		Charting.Reporting.ChartReportingViewProps<T>,
		Charting.Reporting.ChartReportingExportProps<T>,
		PropsWithChildren<any> {
	
	data: D,
	tableComponent: ReactNode,
	chartComponent: ReactNode,
	exportMethods: ReturnType<Charting.Reporting.ChartReportExportHookMethod<T, D>>,
}

const ChartReportWithControlsLayoutDefaultProps = {
	data:           {},
	tableComponent: null,
	chartComponent: null,
	exportMethods:  {
		generateCSVData:         () => ( { headers: [], data: [] } ),
		generateReportText:      () => '',
		handleCSVGenerated:      () => {},
		handleCopiedtoClipboard: () => {},
		handleSaveAsImage:       () => {},
	},
} satisfies Partial<ChartReportWithControlsLayoutProps<any, any>>

function ChartReportWithControlsLayout<T extends ChartType, D extends ChartData<T, DistributiveArray<ChartTypeRegistry[T]["defaultDataPoint"]>, unknown>>( { children, ...p }: ChartReportWithControlsLayoutProps<T, D> ){
	
	const props = { ...ChartReportWithControlsLayoutDefaultProps, ...p };
	
	const reportView    = useChartReportingView<T>( { ...props, children } ),
	      reportExport  = useChartReportingExport<T>( props ),
	      exportMethods = props.exportMethods;
	
	const tableViewType = reportView.tableViewActive
	                      ? ChartReportingViewType.TABLE
	                      : ChartReportingViewType.NONTABLE;
	
	/* CHART CONTROLS */
	const viewAndControlBarComponent = (
		
		<UseIf x={ reportView.hasToggleControls || reportExport.hasExportControls } else={ null }>
			
			<ChartReportViewControls
				enableTableToggle={ props.enableTableToggle && ( reportView.hasChartView || reportView.hasCustomView ) }
				enableDataCopy={ reportExport.hasExportControls }
				dataCopyMenuProps={ {
					csvData:             props.enableCSVDownload ? exportMethods.generateCSVData( props.data ) : null,
					onGenerateCSV:       props.enableCSVDownload ? exportMethods.handleCSVGenerated : null,
					copyToClipboardText: props.enableCopyDataText ? exportMethods.generateReportText( props.data ) : null,
					onCopyToClipboard:   props.enableCopyDataText ? exportMethods.handleCopiedtoClipboard : null,
					onSaveImage:         ( props.enableImageDownload && !reportView.tableViewActive ) ? exportMethods.handleSaveAsImage : null,
				} }
				nonTableViewIcon={ (
					reportView.hasChartView
					? props.chartViewIcon ?? 'chart bar outline'
					: props.customViewIcon ?? 'image outline'
				) }
				nonTableViewLabel={ (
					reportView.hasChartView ? 'chart' : 'custom report'
				) }
				viewType={ tableViewType }
				onClickNonTableView={ () => reportView.setTableViewActive( false ) }
				onClickTableView={ () => reportView.setTableViewActive( true ) }
			/>
		
		</UseIf>
	)
	
	return (
		<>
			
			{/* Visualization Chart */ }
			<UseIf x={ reportView.showChartView && reportView.hasChartView }>
				
				<UseIf x={ viewAndControlBarComponent }>
					
					{ viewAndControlBarComponent }
					
					<Divider/>
				
				</UseIf>
				
				{ props.chartComponent }
			
			</UseIf>
			
			{/* Custom Data for Children Callback */ }
			<UseIf x={ reportView.hasCustomView }>
				
				{ _.isFunction( children ) && children(
					props.data,
					props.title,
					reportView.tableViewActive ? props.tableComponent : props.chartComponent,
					viewAndControlBarComponent,
					tableViewType
				) }
			
			</UseIf>
			
			{/* Default Table */ }
			<UseIf x={ ( reportView.showTableView && !reportView.hasCustomView ) || ( !reportView.hasChartView && !reportView.hasCustomView ) }>
				
				<UseIf x={ viewAndControlBarComponent }>
					
					{ viewAndControlBarComponent }
					
					<Divider/>
				
				</UseIf>
				
				{ props.tableComponent }
			
			</UseIf>
		
		</>
	);
}

export {
	ChartReportWithControlsLayout,
	ChartReportWithControlsLayoutDefaultProps,
	type ChartReportWithControlsLayoutProps,
};
